import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, LogOut, Search, Download, ShieldCheck, Zap, Clock } from "lucide-react";

const SALES_STATUSES = ["new", "contacted", "demo_booked", "won", "churned"];
const PLAN_OPTIONS = ["trial", "clinic", "group"];
const FILTER_PLANS = ["trial", "clinic", "group", "pilot"];

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");
const daysLeft = (v) => (v ? Math.max(0, Math.ceil((new Date(v) - new Date()) / 86400000)) : 0);

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [settings, setSettings] = useState({ pilot_contact_name: "", pilot_contact_url: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, g] = await Promise.all([
        api.get("/admin/clinics"),
        api.get("/admin/stats"),
        api.get("/admin/settings"),
      ]);
      setClinics(c.data);
      setStats(s.data);
      setSettings({
        pilot_contact_name: g.data.pilot_contact_name || "",
        pilot_contact_url: g.data.pilot_contact_url || "",
      });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Pilot contact saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const startPilot = async (id) => {
    try {
      const { data } = await api.post(`/admin/clinics/${id}/pilot/start`);
      setClinics((arr) => arr.map((c) => (c.id === id ? { ...c, ...data } : c)));
      if (open?.id === id) setOpen({ ...open, ...data });
      toast.success("30-day pilot started");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const extendPilot = async (id) => {
    const input = window.prompt("Extend the pilot by how many days?", "14");
    if (input === null) return;
    const days = parseInt(input, 10);
    if (!days || days < 1) {
      toast.error("Enter a valid number of days");
      return;
    }
    try {
      const { data } = await api.post(`/admin/clinics/${id}/pilot/extend`, { days });
      setClinics((arr) => arr.map((c) => (c.id === id ? { ...c, ...data } : c)));
      if (open?.id === id) setOpen({ ...open, ...data });
      toast.success(`Pilot extended by ${days} days`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const createClinic = async (payload) => {
    const { data } = await api.post("/admin/clinics", payload);
    setClinics((arr) => [data, ...arr]);
    toast.success(`${data.clinic_name} created. Invite email sent`);
    return data;
  };

  const deleteClinic = async (id) => {
    const { data } = await api.delete(`/admin/clinics/${id}`);
    setClinics((arr) => arr.filter((c) => c.id !== id));
    setOpen(null);
    toast.success(`${data.clinic_name} deleted (${data.leads_deleted} leads removed)`);
  };

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/clinics/${id}`, patch);
      setClinics((arr) => arr.map((c) => (c.id === id ? { ...c, ...data } : c)));
      if (open?.id === id) setOpen({ ...open, ...data });
      toast.success("Updated");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const exportCSV = () => {
    const headers = [
      "clinic_name",
      "owner_first_name",
      "owner_last_name",
      "email",
      "owner_phone",
      "country",
      "locations_count",
      "monthly_case_volume",
      "referral_source",
      "plan",
      "plan_status",
      "payment_verified",
      "sales_status",
      "transformations_used",
      "transformations_quota",
      "total_previews",
      "created_at",
    ];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = clinics.map((c) => headers.map((h) => escape(c[h])).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `toothpix-clinics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const filtered = clinics.filter((c) => {
    if (filterPlan && c.plan !== filterPlan) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [
        c.clinic_name,
        c.email,
        c.owner_first_name,
        c.owner_last_name,
        c.country,
        c.clinic_slug,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  if (!user) return null;

  return (
    <div className="min-h-screen bg-bone-100">
      <nav className="border-b border-bone-200 bg-forest-600 text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles size={20} strokeWidth={1.5} />
            <span className="font-heading text-xl">Toothpix</span>
            <span className="text-[10px] uppercase tracking-[0.2em] bg-white/15 px-2 py-1 ml-3 flex items-center gap-1"><ShieldCheck size={11} /> Admin Console</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/dashboard" className="hover:text-bone-200 text-bone-100/80" data-testid="admin-to-dashboard">My clinic →</Link>
            <button onClick={() => { logout(); navigate("/"); }} className="ml-2" aria-label="Logout" data-testid="admin-logout">
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-[1600px] mx-auto p-6 md:p-10">
        <div className="overline">Toothpix HQ</div>
        <h1 className="font-heading text-4xl md:text-5xl text-forest-700 mt-2">All clinics on Toothpix</h1>
        <p className="text-sm text-forest-400 mt-2">Pipeline, plan status, and signups across the entire platform.</p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mt-8">
          <StatCard label="Total clinics" value={stats?.total_clinics} />
          <StatCard label="Paying" value={stats?.paying_clinics} accent />
          <StatCard label="On trial" value={stats?.trialing_clinics} />
          <StatCard label="MRR" value={stats ? `$${stats.mrr_estimate}` : "—"} />
          <StatCard label="Patient previews" value={stats?.total_previews} />
        </div>

        {/* Controls */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clinic, email, owner…"
                className="input-flat pl-9"
                data-testid="admin-search"
              />
            </div>
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="input-flat bg-white w-40"
              data-testid="admin-filter-plan"
            >
              <option value="">All plans</option>
              {FILTER_PLANS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="admin-create-clinic">
              + Create clinic
            </button>
            <button onClick={exportCSV} className="btn-secondary" data-testid="admin-export-csv">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Pilot contact (account-level default; per-clinic override in each clinic's details) */}
        <div className="mt-6 bg-white border border-bone-200 p-4 md:p-5 flex flex-col md:flex-row md:items-end gap-4" data-testid="pilot-settings">
          <div className="flex items-center gap-2 md:pb-2">
            <Zap size={16} className="text-forest-600" />
            <div>
              <div className="overline">Pilot contact</div>
              <div className="text-xs text-forest-400 mt-0.5">Shown to pilot clinics in their final days.</div>
            </div>
          </div>
          <div className="flex-1">
            <label className="label-flat">Contact name</label>
            <input
              value={settings.pilot_contact_name}
              onChange={(e) => setSettings({ ...settings, pilot_contact_name: e.target.value })}
              placeholder="e.g. Sarah at Toothpix"
              className="input-flat"
              data-testid="settings-pilot-name"
            />
          </div>
          <div className="flex-1">
            <label className="label-flat">Contact link</label>
            <input
              value={settings.pilot_contact_url}
              onChange={(e) => setSettings({ ...settings, pilot_contact_url: e.target.value })}
              placeholder="https://cal.com/… or mailto:…"
              className="input-flat"
              data-testid="settings-pilot-url"
            />
          </div>
          <button onClick={saveSettings} className="btn-primary !py-2.5 !px-5" data-testid="settings-save">
            Save
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white border border-bone-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-clinics-table">
            <thead>
              <tr className="text-left text-forest-400 border-b border-bone-200">
                <th className="px-4 py-3 font-normal overline">Clinic</th>
                <th className="px-4 py-3 font-normal overline">Owner</th>
                <th className="px-4 py-3 font-normal overline">Plan</th>
                <th className="px-4 py-3 font-normal overline">Used / Quota</th>
                <th className="px-4 py-3 font-normal overline">Pipeline</th>
                <th className="px-4 py-3 font-normal overline">Joined</th>
                <th className="px-4 py-3 font-normal overline"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-forest-400 text-sm">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-forest-400 text-sm">No clinics match these filters.</td></tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id} className="border-b border-bone-200 last:border-0 hover:bg-bone-100/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-forest-700 flex items-center gap-2">
                        {c.clinic_name}
                        {c.is_demo && (
                          <span className="text-[10px] uppercase tracking-[0.15em] bg-clay-400/20 text-clay-600 px-1.5 py-0.5" data-testid={`demo-badge-${c.id}`}>Demo</span>
                        )}
                      </div>
                      <div className="text-xs text-forest-400">{c.email}</div>
                    </td>
                    <td className="px-4 py-3 text-forest-400">
                      <div className="text-forest-700">{c.owner_first_name} {c.owner_last_name}</div>
                      <div className="text-xs">{c.country}</div>
                    </td>
                    <td className="px-4 py-3">
                      {c.plan === "pilot" ? (
                        <div data-testid={`pilot-cell-${c.id}`}>
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-forest-600 text-white">
                            <Zap size={10} /> Pilot
                          </span>
                          <div className={`mt-1 text-[11px] flex items-center gap-1 ${c.pilot_read_only ? "text-clay-600 font-medium" : "text-forest-400"}`}>
                            <Clock size={10} />
                            {c.pilot_read_only
                              ? "Read-only · ended"
                              : `Ends ${fmtDate(c.pilot_ends_at)} · ${daysLeft(c.pilot_ends_at)}d`}
                          </div>
                          <div className="mt-1 flex gap-2">
                            <button onClick={() => extendPilot(c.id)} className="text-[11px] underline text-forest-600" data-testid={`extend-pilot-${c.id}`}>
                              Extend
                            </button>
                            <button onClick={() => update(c.id, { plan: "clinic", plan_status: "active" })} className="text-[11px] underline text-forest-400" data-testid={`convert-${c.id}`}>
                              Convert to paid
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <select
                            value={c.plan}
                            onChange={(e) => update(c.id, { plan: e.target.value })}
                            className="border border-bone-200 px-2 py-1 text-xs bg-white"
                            data-testid={`plan-select-${c.id}`}
                          >
                            {PLAN_OPTIONS.map((p) => (<option key={p} value={p}>{p}</option>))}
                          </select>
                          {c.plan !== "trial" && (
                            <label className="flex items-center gap-1 mt-1 text-[11px] text-forest-400">
                              <input
                                type="checkbox"
                                checked={!!c.payment_verified}
                                onChange={(e) => update(c.id, { payment_verified: e.target.checked })}
                                data-testid={`verify-${c.id}`}
                              />
                              Stripe verified
                            </label>
                          )}
                          <button onClick={() => startPilot(c.id)} className="block mt-1 text-[11px] underline text-forest-600" data-testid={`start-pilot-${c.id}`}>
                            Start 30-day pilot
                          </button>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-forest-400 text-xs">
                      {c.transformations_used} / {c.transformations_quota === -1 ? "∞" : c.transformations_quota}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={c.sales_status || "new"}
                        onChange={(e) => update(c.id, { sales_status: e.target.value })}
                        className="border border-bone-200 px-2 py-1 text-xs bg-white"
                        data-testid={`pipeline-${c.id}`}
                      >
                        {SALES_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-forest-400 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setOpen(c)} className="text-forest-600 text-xs hover:text-forest-700" data-testid={`view-${c.id}`}>Details →</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {open && (
        <ClinicDetail
          clinic={open}
          onClose={() => setOpen(null)}
          onStartPilot={startPilot}
          onExtendPilot={extendPilot}
          onUpdate={update}
          onDelete={deleteClinic}
        />
      )}
      {showCreate && (
        <CreateClinicModal onClose={() => setShowCreate(false)} onCreate={createClinic} />
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`p-6 border ${accent ? "bg-forest-600 text-white border-forest-600" : "bg-white border-bone-200"}`}>
      <div className={`overline ${accent ? "!text-bone-200/70" : ""}`}>{label}</div>
      <div className="font-body font-semibold text-3xl mt-3 tabular-nums tracking-tight">{value ?? "—"}</div>
    </div>
  );
}

function ClinicDetail({ clinic, onClose, onStartPilot, onExtendPilot, onUpdate, onDelete }) {
  const [contact, setContact] = useState({
    pilot_contact_name: clinic.pilot_contact_name || "",
    pilot_contact_url: clinic.pilot_contact_url || "",
  });
  const [treatment, setTreatment] = useState(clinic.treatment_name || "clear aligners");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isPilot = clinic.plan === "pilot";
  const f = (l, v) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-bone-200 last:border-0 text-sm">
      <div className="text-forest-400 overline">{l}</div>
      <div className="col-span-2 text-forest-700 break-words">{v || "—"}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-40 bg-forest-700/70 flex items-start justify-end" onClick={onClose}>
      <div className="bg-white max-w-md w-full h-full overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-clinic-detail">
        <div className="px-6 py-5 border-b border-bone-200 flex items-center justify-between">
          <div>
            <div className="overline">Clinic</div>
            <div className="font-heading text-2xl text-forest-700">{clinic.clinic_name}</div>
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-2xl leading-none" data-testid="admin-detail-close">×</button>
        </div>

        {/* Pilot controls */}
        <div className="p-6 border-b border-bone-200 bg-bone-100/40">
          <div className="overline flex items-center gap-1"><Zap size={12} /> Pilot</div>
          {isPilot ? (
            <div className="mt-3 text-sm">
              <div className={clinic.pilot_read_only ? "text-clay-600 font-medium" : "text-forest-700"} data-testid="detail-pilot-status">
                {clinic.pilot_read_only
                  ? "Ended. Clinic is read-only until it upgrades"
                  : `${daysLeft(clinic.pilot_ends_at)} days left · ends ${fmtDate(clinic.pilot_ends_at)}`}
              </div>
              <div className="text-xs text-forest-400 mt-1">Started {fmtDate(clinic.pilot_started_at)}</div>
              <button onClick={() => onExtendPilot(clinic.id)} className="btn-secondary !py-2 !px-4 text-xs mt-3" data-testid="detail-extend-pilot">
                Extend pilot
              </button>
            </div>
          ) : (
            <button onClick={() => onStartPilot(clinic.id)} className="btn-primary !py-2 !px-4 text-xs mt-3" data-testid="detail-start-pilot">
              Start 30-day pilot
            </button>
          )}

          {/* Per-clinic contact override */}
          <div className="mt-5">
            <div className="overline">Pilot contact override</div>
            <div className="text-xs text-forest-400 mt-0.5 mb-2">Leave blank to use the account-level default.</div>
            <input
              value={contact.pilot_contact_name}
              onChange={(e) => setContact({ ...contact, pilot_contact_name: e.target.value })}
              placeholder="Contact name"
              className="input-flat mb-2"
              data-testid="detail-contact-name"
            />
            <input
              value={contact.pilot_contact_url}
              onChange={(e) => setContact({ ...contact, pilot_contact_url: e.target.value })}
              placeholder="Contact link (https:// or mailto:)"
              className="input-flat"
              data-testid="detail-contact-url"
            />
            <button
              onClick={() => onUpdate(clinic.id, contact)}
              className="btn-secondary !py-2 !px-4 text-xs mt-2"
              data-testid="detail-save-contact"
            >
              Save override
            </button>
          </div>

          {/* Per-clinic treatment name */}
          <div className="mt-5">
            <div className="overline">Treatment name</div>
            <div className="text-xs text-forest-400 mt-0.5 mb-2">Used on the patient page and in every email for this clinic.</div>
            <input
              value={treatment}
              onChange={(e) => setTreatment(e.target.value)}
              placeholder="clear aligners"
              className="input-flat"
              data-testid="detail-treatment-name"
            />
            <button
              onClick={() => onUpdate(clinic.id, { treatment_name: treatment })}
              className="btn-secondary !py-2 !px-4 text-xs mt-2"
              data-testid="detail-save-treatment"
            >
              Save treatment
            </button>
          </div>
        </div>

        <div className="p-6">
          {f("Email", clinic.email)}
          {f("Owner", `${clinic.owner_first_name} ${clinic.owner_last_name}`)}
          {f("Title", clinic.owner_title)}
          {f("Owner phone", clinic.owner_phone)}
          {f("Country", clinic.country)}
          {f("Locations", clinic.locations_count)}
          {f("Monthly cases", clinic.monthly_case_volume)}
          {f("Source", clinic.referral_source)}
          {f("Treatment", clinic.treatment_name)}
          {f("Website", clinic.website)}
          {f("Plan", clinic.plan)}
          {f("Plan status", clinic.plan_status)}
          {f("Stripe verified", clinic.payment_verified ? "Yes" : "No")}
          {f("Usage", `${clinic.transformations_used} / ${clinic.transformations_quota === -1 ? "∞" : clinic.transformations_quota}`)}
          {f("Patient link", `/preview/${clinic.clinic_slug}`)}
          {f("Booking URL", clinic.booking_url)}
          {f("Joined", clinic.created_at)}

          {/* Demo flag */}
          <label className="flex items-center gap-2 mt-6 text-sm text-forest-700 cursor-pointer" data-testid="detail-demo-toggle">
            <input
              type="checkbox"
              checked={!!clinic.is_demo}
              onChange={(e) => onUpdate(clinic.id, { is_demo: e.target.checked })}
            />
            Demo account (excluded from all HQ stats, labeled in the list)
          </label>

          {/* Danger zone */}
          <div className="mt-8 border border-clay-600/40 bg-clay-400/10 p-4">
            <div className="overline !text-clay-600">Danger zone</div>
            {!confirmingDelete ? (
              <button
                onClick={() => setConfirmingDelete(true)}
                className="mt-2 text-sm text-clay-600 underline underline-offset-4"
                data-testid="detail-delete-clinic"
              >
                Delete this clinic
              </button>
            ) : (
              <div className="mt-2" data-testid="delete-confirm">
                <p className="text-sm text-forest-700">
                  Permanently delete <b>{clinic.clinic_name}</b> and all its data, including{" "}
                  <b>{clinic.total_previews ?? 0} lead{(clinic.total_previews ?? 0) === 1 ? "" : "s"}</b>,
                  their previews, and any scheduled follow-up emails? This cannot be undone.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className="btn-secondary !py-2 !px-4 text-xs"
                    data-testid="delete-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => { setDeleting(true); try { await onDelete(clinic.id); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setDeleting(false); } }}
                    disabled={deleting}
                    className="!py-2 !px-4 text-xs bg-clay-600 text-white hover:bg-clay-700 disabled:opacity-50"
                    data-testid="delete-confirm-btn"
                  >
                    {deleting ? "Deleting…" : "Delete permanently"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateClinicModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ clinic_name: "", email: "", website: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clinic_name.trim() || !form.email.trim()) {
      toast.error("Clinic name and email are required");
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        clinic_name: form.clinic_name.trim(),
        email: form.email.trim(),
        website: form.website.trim(),
      });
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-forest-700/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="create-clinic-modal">
        <div className="px-6 py-5 border-b border-bone-200 flex items-center justify-between">
          <div>
            <div className="overline">New clinic</div>
            <div className="font-heading text-2xl text-forest-700">Create a clinic</div>
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-2xl leading-none" data-testid="create-clinic-close">×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <p className="text-sm text-forest-400">
            Starts a <b>30-day pilot</b> immediately (full access + embed) and emails the clinic
            their login and a secure link to set their password. No self-signup needed.
          </p>
          <div>
            <label className="label-flat">Clinic name</label>
            <input
              className="input-flat"
              value={form.clinic_name}
              onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
              data-testid="create-clinic-name"
              autoFocus
            />
          </div>
          <div>
            <label className="label-flat">Email</label>
            <input
              type="email"
              className="input-flat"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="create-clinic-email"
            />
          </div>
          <div>
            <label className="label-flat">Website (optional)</label>
            <input
              className="input-flat"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://"
              data-testid="create-clinic-website"
            />
          </div>
          <div className="pt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2.5 !px-5 text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary !py-2.5 !px-5 text-sm" data-testid="create-clinic-submit">
              {saving ? "Creating…" : "Create & start pilot"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
